import React, { useEffect, useRef, useState } from "react";
import { useSurface, SurfaceProvider } from "../lib/surface-context";
import { surfaceClasses } from "../lib/surface-classes";
import { useFluidHover, useRegisterFluidHoverItem } from "../lib/use-fluid-hover";
import { FluidHoverHighlight } from "./FluidHoverHighlight";
import { ScrollArea } from "./ScrollArea";

/* Font Awesome 4 is this app's only icon font and its clock glyph does not
   match the line weight of the rest of this control, so the glyph is inlined
   the way StatCard.js inlines its trend arrows. */
const ClockGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);

/* The API takes "HH:MM:SS" for time_from / time_to, but a native time input
   emits "HH:MM" whenever `step` is a whole minute or coarser. Normalising here
   makes `step` a purely visual choice: whatever the seconds field is doing,
   the caller always gets the wire format back. */
const toHms = (value) => {
  if (!value) return value;
  const parts = value.split(":");
  while (parts.length < 3) parts.push("00");
  return parts.join(":");
};

const toMinutes = (hms) => {
  if (!hms) return null;
  const [h, m] = hms.split(":");
  return Number(h) * 60 + Number(m);
};

const fromMinutes = (total) => {
  const h = String(Math.floor(total / 60)).padStart(2, "0");
  const m = String(total % 60).padStart(2, "0");
  return h + ":" + m + ":00";
};

/* Labels are built by hand rather than through moment/toLocaleTimeString so the
   dropdown reads the same for every user regardless of their locale — the old
   react-bootstrap-time-picker this replaces listed "09:00 AM" too. */
const label12Hour = (hms) => {
  const total = toMinutes(hms);
  const hours24 = Math.floor(total / 60);
  const minutes = total % 60;
  const meridiem = hours24 < 12 ? "AM" : "PM";
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return (
    String(hours12).padStart(2, "0") +
    ":" +
    String(minutes).padStart(2, "0") +
    " " +
    meridiem
  );
};

const buildOptions = (min, max, interval, after) => {
  const floor = toMinutes(min) ?? 0;
  const end = toMinutes(max) ?? 23 * 60 + 59;
  const stride = interval > 0 ? interval : 30;
  /* `after` is exclusive: End Time may not equal Start Time, only beat it. */
  const bound = toMinutes(after);
  const start = bound === null ? floor : Math.max(floor, bound + 1);
  const first = Math.ceil((start - floor) / stride) * stride + floor;
  const out = [];
  for (let m = first; m <= end; m += stride) out.push(fromMinutes(m));
  return out;
};

/**
 * A time input whose value is the "HH:MM:SS" string the EMS API already takes
 * for time_from / time_to — there is no seconds-since-midnight round-trip to
 * do, unlike react-bootstrap-time-picker.
 *
 *   step={60}  (default) seconds hidden, because a 12-hour "09:00:00 AM" needs
 *              ~119px of field and a col-md-3 cell affords ~87px, which clips
 *              the meridiem. onChange still reports "HH:MM:SS" either way.
 *   interval   minutes between dropdown entries (default 30)
 *
 * The clock opens OUR OWN dropdown rather than the browser's showPicker():
 * Safari on macOS has no native picker for type="time" at all, so the native
 * one is not a picker we can rely on. The input itself stays native, so typing
 * and keyboard entry work exactly as before.
 *
 * `after` is an exclusive lower bound — pass Start Time to End Time and the
 * list begins at the next slot, never at the same minute.
 *
 * min/max bound the dropdown. They are forwarded to the input too, but the
 * browser does not enforce them against typed input, so a caller that needs a
 * real bound still has to check it — see Utils.isTimeAfter.
 */
const TimeField = ({
  value = "",
  onChange,
  step = 60,
  min,
  max,
  interval = 30,
  after,
  disabled = false,
  invalid = false,
  id,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  // A dropdown: two surface steps up, shadow pinned to 3. One fluid-hover fill
  // glides between the options, rising out of the selected one.
  const level = Math.min(useSurface() + 2, 8);
  const hover = useFluidHover(listRef);

  /* Sidebar.js:195 and DateField both dismiss on document mousedown. Match
     them rather than introduce a third dismissal idiom in the same app. */
  useEffect(() => {
    if (!open) return undefined;

    const onDocMouseDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  /* Open on 09:00 in a list that starts at midnight and you are looking at the
     wrong end of 18 entries. */
  useEffect(() => {
    if (!open || !listRef.current) return;
    const selected = listRef.current.querySelector(".is-selected");
    if (selected && selected.scrollIntoView) {
      selected.scrollIntoView({ block: "center" });
    }
  }, [open]);

  const options = buildOptions(min, max, interval, after);
  const current = value ? toHms(value) : "";
  /* The browser validates against the raw min, which would still let an
     end time equal to the start through. Hand it the first time we allow. */
  const inputMin = options.length ? options[0] : min;

  const pick = (hms) => {
    if (onChange) onChange(hms);
    setOpen(false);
  };

  return (
    <div className={"df-time" + (className ? " " + className : "")} ref={wrapRef}>
      <button
        type="button"
        className="df-time-btn"
        onClick={() => setOpen((prev) => !prev)}
        disabled={disabled}
        tabIndex={-1}
        aria-label="Choose a time"
        aria-expanded={open}
      >
        <ClockGlyph />
      </button>
      <input
        type="time"
        id={id}
        className={"df-time-input" + (invalid ? " is-invalid" : "")}
        value={value}
        step={step}
        min={inputMin}
        max={max}
        disabled={disabled}
        onChange={(event) => onChange && onChange(toHms(event.target.value))}
      />
      {open ? (
        <div className="df-time-pop">
          <div className={"df-panel df-time-panel " + surfaceClasses(level, 3)}>
            <SurfaceProvider value={level}>
              <ScrollArea viewportClassName="df-time-viewport scroll-fade">
                <div
                  className="df-time-list"
                  ref={listRef}
                  role="listbox"
                  {...hover.handlers}
                >
                  <FluidHoverHighlight
                    hover={hover}
                    from={hover.itemRects[options.indexOf(current)]}
                    className="df-time-hover"
                  />
                  {options.length === 0 ? (
                    <p className="df-time-empty">No later time available</p>
                  ) : null}
                  {options.map((hms, index) => (
                    <TimeOption
                      key={hms}
                      index={index}
                      registerItem={hover.registerItem}
                      selected={hms === current}
                      onClick={() => pick(hms)}
                    >
                      {label12Hour(hms)}
                    </TimeOption>
                  ))}
                </div>
              </ScrollArea>
            </SurfaceProvider>
          </div>
        </div>
      ) : null}
    </div>
  );
};

/** One row of the time list, registered with the list's fluid hover. */
const TimeOption = ({ index, registerItem, selected, onClick, children }) => {
  const ref = useRef(null);
  useRegisterFluidHoverItem(registerItem, index, ref);
  return (
    <button
      ref={ref}
      type="button"
      role="option"
      aria-selected={selected}
      className={"df-time-option" + (selected ? " is-selected" : "")}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

export default TimeField;
